import type { CompetitionType } from "./queries/competitions";
import type { Membership, MembershipKind, Season } from "./queries/memberships";
import { hasAnyGlobalRole, hasFederationRole, hasTeamRole, type User } from "../auth/queries";
import type { ID } from "./id";

/**
 * Whether a team may file a přihláška at all, before anybody is picked.
 *
 * An MČR start admits registered members only; the server refuses a guest
 * per competitor (internal/service/entry/validate.go). A team with nobody
 * registered in the active season therefore cannot file one, and the
 * wizard used to let it in anyway, to a picker that read „Nikdo neodpovídá
 * filtru" with no filter set. This lets every surface say why beforehand.
 *
 * The frontend mirror of a server rule, used only to hide dead controls.
 */

/** Mirrors `models.CompetitionType.IsMCR`. Positive on purpose: a new
 * type must not silently start requiring a registration. */
export function isMcr(type: CompetitionType): boolean {
  return type === "mcr_qualification" || type === "mcr_final";
}

export function needsSeasonRegistration(
  type: CompetitionType,
  hasRegisteredMembers: boolean,
): boolean {
  return isMcr(type) && !hasRegisteredMembers;
}

/**
 * The twin of `entry.TeamMayEnter` (Go): may a team file an entry here at
 * all? Pohárovky are open to anyone; MČR is ČNSMS's own championship, so a
 * team outside it is refused as a TEAM, before anybody is picked.
 *
 * Kept in step with the Go resolver by
 * `e2e/specs/prihlaska/eligibility.spec.ts`.
 */
export function teamMayEnter(type: CompetitionType, teamIsCmf: boolean): boolean {
  return isMcr(type) ? teamIsCmf : true;
}

/**
 * Why a club cannot file a přihláška here, or null when it can. Two
 * different refusals that a single boolean used to blur:
 *
 * - `not_our_competition` — MČR is ČNSMS's own championship and this team
 *   belongs elsewhere. No registration would change it, so no surface may
 *   offer one.
 * - `needs_registration` — a ČNSMS team with nobody registered in the
 *   season. Fixable, and „Registrace do sezóny" is where.
 *
 * Every club surface asks this, so the cards, the přihlášky page, the
 * dashboard and the wizard cannot disagree about which sentence to show.
 */
export type ClubEntryBlock = "not_our_competition" | "needs_registration" | null;

export function clubEntryBlock(
  type: CompetitionType,
  teamIsCmf: boolean,
  hasRegisteredMembers: boolean,
): ClubEntryBlock {
  if (!teamMayEnter(type, teamIsCmf)) return "not_our_competition";
  if (needsSeasonRegistration(type, hasRegisteredMembers)) return "needs_registration";
  return null;
}

/** The twin of `entry.CompetitorMayEnter`: may this roster row be named? */
export function competitorMayEnter(type: CompetitionType, kind: MembershipKind): boolean {
  return isMcr(type) ? kind === "registered" : true;
}

/**
 * „Is anybody registered", asked of the active-season roster
 * (`useTeamMembers(teamId)`). The club pages and the wizard all ask it this
 * way so they cannot disagree. `team.state_issues` would not do there: the
 * server fills it for ACTIVE teams only, so a pending or suspended team
 * would read as registered on the list and be refused by the wizard.
 */
export function hasRegisteredMember(members: Pick<Membership, "kind">[]): boolean {
  return members.some((m) => m.kind === "registered");
}

/**
 * Whether „Registrace do sezóny" leads anywhere for this user: the
 * workspace redirects away outside the registration window, except for a
 * membership admin, who may register year-round. Team-blind, for the
 * sidebar; a button for one team uses `seasonRegistrationAccess`.
 */
export function mayOpenSeasonRegistration(
  activeSeason: Season | undefined,
  user: User | null | undefined,
): boolean {
  return (
    !!activeSeason &&
    (activeSeason.registration_open || hasAnyGlobalRole(user, "admin", "members_admin"))
  );
}

/** Who may open this team's Registrace do sezóny at all. */
export function canRegisterTeam(
  user: User | null | undefined,
  teamId: ID,
  federationId?: ID | null,
): boolean {
  return (
    hasAnyGlobalRole(user, "admin", "members_admin") ||
    hasTeamRole(user, teamId, "team_leader") ||
    (federationId ? hasFederationRole(user, federationId, "federation_admin") : false)
  );
}

export type SeasonRegistrationAccess = "open" | "forbidden" | "closed";

/**
 * Whether a „Registrace do sezóny" button for one team leads anywhere, and
 * if not, why — in the order SeasonRegistrationPage refuses: somebody who
 * may not manage the team first, then anyone below members_admin outside
 * the window. The two reasons need different sentences: a coach told the
 * window is closed would wait for it to open.
 */
export function seasonRegistrationAccess(
  activeSeason: Season | undefined,
  user: User | null | undefined,
  teamId: ID,
  federationId?: ID | null,
): SeasonRegistrationAccess {
  if (!canRegisterTeam(user, teamId, federationId)) return "forbidden";
  return mayOpenSeasonRegistration(activeSeason, user) ? "open" : "closed";
}

/** The sentence beside a missing „Registrace do sezóny" button. */
export const SEASON_REGISTRATION_BLOCKED: Record<Exclude<SeasonRegistrationAccess, "open">, string> = {
  closed: "Registrační okno je uzavřené, obraťte se na správce členské základny.",
  forbidden: "Registraci do sezóny provádí vedoucí.",
};
